import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Mail, Lock, Eye, EyeOff, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/j9ko1qni_70964783-6509-4567-B714-2BDADDC4579E%20%281%29.PNG";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data);
      toast.success("Welcome back!");
      
      // Check if user has profile
      const meResponse = await axios.get(`${API}/auth/me`);
      if (meResponse.data.has_profile) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const response = await axios.post(`${API}/auth/demo-login`);
      login(response.data);
      toast.success("Demo login successful! Explore with full premium access.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Demo login failed");
    } finally {
      setDemoLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Colorful Header Bar - Same as Landing */}
      <header className="relative z-50 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between px-6 py-3">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="KiddiPlay" className="w-10 h-10 rounded-full border-2 border-white/30" />
              <span className="text-xl font-bold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
                KiddiPlay Club
              </span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Link to="/register">
                <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-6">
                  Join Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-64px)]">
        {/* Decorative blobs */}
        <div className="blob blob-pink w-80 h-80 -top-40 -right-40 fixed" />
        <div className="blob blob-blue w-64 h-64 bottom-20 -left-32 fixed" />

        {/* Left side - Form */}
        <div className="flex-1 flex items-center justify-center p-8 relative">
          <div className="w-full max-w-md relative z-10">
            <h1 className="text-2xl md:text-3xl font-bold mb-2 text-center" style={{ fontFamily: 'Fredoka, Nunito, sans-serif', color: '#6B4D8A' }}>
              Welcome Back to KiddiPlay Club!
            </h1>
            <p className="text-slate-600 mb-8 text-center">Sign in to continue connecting with families</p>

            {/* Google Login */}
            <Button
              variant="outline"
              className="w-full rounded-xl py-6 mb-6 border-2 hover:bg-slate-50"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#FFF9F5] text-slate-500">or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="hello@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-12 rounded-xl bg-white border-slate-200 focus:border-sky-400 focus:ring-teal-200"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 rounded-xl bg-white border-slate-200 focus:border-sky-400 focus:ring-teal-200"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-teal-500 hover:bg-teal-600 text-white rounded-xl py-6 text-lg font-medium"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-center mt-6 text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" className="text-purple-500 hover:text-purple-600 font-medium" data-testid="register-link">
              Sign up
            </Link>
          </p>

          {/* Demo Login */}
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
            <p className="text-sm text-slate-600 mb-3 text-center">
              Just want to explore? Try our demo account with full access!
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl py-5 border-2 border-purple-300 text-purple-600 hover:bg-purple-50 font-medium"
              onClick={handleDemoLogin}
              disabled={demoLoading}
              data-testid="demo-login-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              {demoLoading ? "Loading Demo..." : "Try Demo Account"}
            </Button>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1648090272983-440e86555e8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBzd2ltbWluZyUyMHBvb2wlMjBraWRzJTIwc3VtbWVyJTIwaGFwcHl8ZW58MHx8fHwxNzgyNjk0ODc5fDA&ixlib=rb-4.1.0&q=85"
          alt="Happy boy in swimming pool with inflatable duck"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFF9F5] via-transparent to-transparent" />
        
        {/* Fun overlay */}
        <div className="absolute bottom-12 right-12">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-xl max-w-xs">
            <p className="text-slate-700 text-sm font-medium">
              "Finally found families who get the chaos! The kids have a blast together."
            </p>
            <p className="text-purple-600 text-xs font-medium mt-2">— Emma, Manchester</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login;